import React, {useCallback} from 'react';
import styled, {createGlobalStyle, keyframes} from 'styled-components';
import {Image} from '../../images';
import {Button} from '../..';
import {Typography} from '../../typography';
import {CreatorsFeedItemProps, CreatorFeedProps} from '../CreatorFeed.organism';
import {CenteredGrid, GridContainer} from '../../grid';
import Grid from '@material-ui/core/Grid';
import {CreatorFeedDetailCloseBtn} from './CreatorFeedDetail.CloseBtn.atom';
import {CreatorFeedDetailBackground} from './CreatorFeedDetail.Background.atom';
import {CreatorFeedDetailImages} from './CreatorFeedDetail.Images.molecule';
import {VerticalSpacer} from '../../spacer';
import {
    UeThemeProvider,
    useBreakpointDown,
    useBreakpointOnly,
    AccentColorProps,
    useProperDarkTheme
} from 'epic-ue-theme';
import {CreatorFeedDetailBrowser} from './CreatorFeedDetail.Browser.atom';
import {CreatorFeedDetailCarousel} from './CreatorFeedDetail.Carousel.atom';
import root from 'window-or-global';
import {TruncateTextParagraph} from '../../text/TruncateTextParagraph';
import {useActiveCreator} from '../creatorfeed.hooks';

export const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const GlobalStyle = createGlobalStyle`
    body {
        overflow: hidden;
    }
`;

const Styled = styled.div`
    animation: ${fadeIn} 1s ${(p) => p.theme.easing};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        opacity: 1;
    }

    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;

    .ue-grid-container,
    .ue-centered-grid.lgup {
        min-height: 100vh;
        max-height: -webkit-fill-available;
    }

    .paragraph {
        color: ${(p) => p.theme.palette.neutral.stone};
    }

    color: ${(p) => p.theme.palette.common.white};

    .MuiGrid-grid-sm-12 {
        min-width: 100%;
    }

    .MuiButton-label {
        color: ${(p) => p.theme.palette.common.white};
    }

    .creator-feed-detail-browser {
        right: 48px;
    }
`;

const Avatar = styled.div`
    display: inline-block;
    width: 110px;
    margin: 0;
    img {
        border-radius: 110px;
    }
    ${(p) => p.theme.breakpoints.down('md')} {
        width: 56px;
        img {
            border-radius: 56px;
        }
    }
`;

const ContentWrapMd = styled.div`
    display: inline-block;
    position: relative;
    top: -8px;
    ${(p) => p.theme.breakpoints.up('md')} {
        width: calc(100% - 56px - 24px);
    }
    a {
        cursor: pointer;
    }

    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }

    ${(p) => p.theme.breakpoints.down('sm')} {
        .image-wrapper {
            top: -8px;
        }
    }
`;

const DetailBrowser = styled.div`
    ${(p) => p.theme.breakpoints.down('sm')} {
        position: absolute;
        bottom: 10px;
        left: 0;
        width: 100%;
    }
`;

const ScrollWrap = styled.div`
    ${(p) => p.theme.breakpoints.down('sm')} {
        max-height: calc(100% - 50px);
        overflow: auto;
    }
    height: 100vh;
    max-height: -webkit-fill-available;
`;

const Flex = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const CreatorFeedDetailView = ({
    title,
    ratio,
    caption,
    imageSrc,
    avatarImageSrc,
    creatorBtn,
    description,
    detailImages = [],
    cards = [],
    browseLabel,
    accentColor = 'sky'
}: CreatorsFeedItemProps & CreatorFeedProps & AccentColorProps): JSX.Element => {
    const isSm = useBreakpointDown('sm');
    const isMd = useBreakpointOnly('md');
    const darkTheme = useProperDarkTheme();
    const {setActiveCreator} = useActiveCreator();

    const onClick = useCallback(() => {
        setActiveCreator(undefined);
    }, [setActiveCreator]);

    const btn = creatorBtn.href && creatorBtn.label && (
        <>
            <VerticalSpacer variant="custom" spacing={4} />
            <Button href={creatorBtn.href} type={creatorBtn.type || 'secondary'} onClick={onClick}>
                {creatorBtn.label}
            </Button>
        </>
    );

    const avatarDownMd = (
        <Avatar>
            <Image
                src={avatarImageSrc}
                ratio="square"
                fit="cover"
                height={56}
                width={56}
                optMaxWidth={120}
            />
        </Avatar>
    );

    const contentWrapDownMd = (
        <ContentWrapMd>
            <Typography variant="h3" big>
                {title}
            </Typography>
            <Typography variant="p" caption>
                {caption}
            </Typography>
        </ContentWrapMd>
    );

    //lg up is default
    let detail = (
        <CenteredGrid justifyContent="center" className="lgup">
            <Grid item lg={3}>
                <Avatar>
                    <Image
                        src={avatarImageSrc}
                        ratio="square"
                        fit="cover"
                        height={110}
                        width={110}
                        optMaxWidth={120}
                    />
                </Avatar>
                <VerticalSpacer variant="custom" spacing={3} />
                <div>
                    <Typography variant="h3" big>
                        {title}
                    </Typography>
                    <VerticalSpacer variant="custom" spacing={0.5} />
                    <Typography variant="p" caption>
                        {caption}
                    </Typography>
                    {btn}
                </div>
            </Grid>

            <Grid item lg={6} style={{height: '100vh'}}>
                <CreatorFeedDetailImages detailImages={detailImages} ratio={ratio} />
            </Grid>

            <Grid item lg={3}>
                <Typography variant="p">{description}</Typography>
                <CreatorFeedDetailBrowser cards={cards} browseLabel={browseLabel} />
            </Grid>
        </CenteredGrid>
    );

    if (isMd) {
        const screenRatio = root.innerWidth / root.innerHeight;
        detail = (
            <CenteredGrid justifyContent="center">
                <Grid item md={screenRatio < 1.5 ? 7 : 5} style={{height: '100vh'}}>
                    <CreatorFeedDetailImages detailImages={detailImages} ratio={ratio} />
                </Grid>
                <Grid item md={screenRatio < 1.5 ? 5 : 7}>
                    <VerticalSpacer variant="custom" spacing={7} />
                    {contentWrapDownMd}
                    {avatarDownMd}
                    <VerticalSpacer variant="custom" spacing={4} />
                    <TruncateTextParagraph maxLines={6} text={description} />
                    {btn}
                    <CreatorFeedDetailBrowser
                        cards={cards}
                        browseLabel={browseLabel}
                        accentColor={accentColor}
                    />
                </Grid>
            </CenteredGrid>
        );
    }

    if (isSm) {
        detail = (
            <CenteredGrid justifyContent="flex-start">
                <Grid item sm={12}>
                    <CreatorFeedDetailCarousel
                        detailImages={detailImages}
                        ratio={ratio}
                        accentColor={accentColor}
                    />
                </Grid>
                <Grid item sm={12}>
                    <Flex>
                        {contentWrapDownMd}
                        {avatarDownMd}
                    </Flex>
                    <VerticalSpacer variant="custom" spacing={2} />
                    <Typography variant="p">{description}</Typography>
                    {btn}
                    <DetailBrowser>
                        <CreatorFeedDetailBrowser
                            cards={cards}
                            browseLabel={browseLabel}
                            accentColor={accentColor}
                        />
                    </DetailBrowser>
                </Grid>
            </CenteredGrid>
        );
    }

    return (
        <UeThemeProvider theme={darkTheme}>
            <Styled className="creator-feed-detail">
                <GlobalStyle />
                <CreatorFeedDetailBackground
                    title={!isSm && !isMd ? title : ''}
                    imageSrc={imageSrc}
                />
                <ScrollWrap>
                    <CreatorFeedDetailCloseBtn accentColor={accentColor} />
                    <GridContainer key={title}>{detail}</GridContainer>
                </ScrollWrap>
            </Styled>
        </UeThemeProvider>
    );
};
